/**
 * Cart Component
 *
 * Displays cart items and allows navigation to checkout
 */

import { useCart } from '@/contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { ShoppingCartIcon, TrashIcon } from '@heroicons/react/24/outline';

export function Cart() {
  const { items, removeItem, getTotalAmount, clearCart } = useCart();
  const navigate = useNavigate();

  const totalAmount = getTotalAmount();
  const formattedTotal = (totalAmount / 100).toFixed(2);

  const handleCheckout = () => {
    navigate('/checkout');
  };

  // Empty cart state
  if (items.length === 0) {
    return (
      <div className="sticky top-20 bg-gray-50 rounded-xl shadow-md p-6 border-2 border-gray-200">
        <div className="flex items-center justify-center mb-4">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCartIcon className="h-6 w-6" />
            Cart
          </h3>
        </div>
        <div className="text-center py-8">
          <ShoppingCartIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg mb-2">Your cart is empty</p>
          <p className="text-gray-400 text-sm">Select a donation type to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div
      id="cart-section"
      className="sticky top-20 bg-primary-100 rounded-xl shadow-md p-6 border-2 border-primary-200"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <ShoppingCartIcon className="h-6 w-6" />
          Cart ({items.length})
        </h3>
        {items.length > 0 && (
          <button onClick={clearCart} className="text-sm text-gray-500 hover:text-red-600">
            Clear All
          </button>
        )}
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-start justify-between p-3 bg-white rounded-lg shadow-sm"
          >
            <div className="flex-1">
              <div className="font-medium text-gray-900">
                {item.donationType?.detailedTextHeader ||
                  `Donation ${item.donationType?.donationType}`}
              </div>
              {item.serviceType && (
                <div className="text-sm text-gray-600">
                  Service: {item.serviceType.detailedTextHeader}
                </div>
              )}
              <div className="text-lg font-semibold text-primary-600 mt-1">
                ${(item.amount / 100).toFixed(2)}
              </div>
            </div>
            <button
              onClick={() => removeItem(item.id)}
              className="ml-2 p-1 text-gray-400 hover:text-red-600"
              aria-label="Remove item"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        ))}
      </div>

      <div className="border-t border-gray-300 pt-4">
        <div className="flex items-center justify-between mb-4">
          <span className="text-lg font-semibold text-gray-900">Total:</span>
          <span className="text-2xl font-bold text-primary-600">${formattedTotal}</span>
        </div>
        <button
          onClick={handleCheckout}
          className="w-full py-3 px-4 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
        >
          Proceed to Checkout
        </button>
      </div>
    </div>
  );
}
